import React, { useState, useRef, useEffect } from 'react';
import '../../../../styles/resume-editor.css';

interface TextFontStyleProps {
  onSettingsChange?: (settings: {
    font_size?: number;
    line_height?: string;
    display_font?: string;
    heading_font?: string;
    text_font?: string;
  }) => void;
  cvUid?: string;
  initialSettings?: {
    font_size?: number;
    line_height?: string;
    display_font?: string;
    heading_font?: string;
    text_font?: string;
  };
}

interface FontPack {
  id: string;
  name: string;
  fonts: string[];
  isMixed?: boolean;
}

export const TextFontStyle: React.FC<TextFontStyleProps> = ({ onSettingsChange, cvUid, initialSettings }) => {
  const [fontSize, setFontSize] = useState(initialSettings?.font_size ?? 10);
  const [lineHeight, setLineHeight] = useState(initialSettings?.line_height ?? '1.15 Condensed');
  const [displayFont, setDisplayFont] = useState(initialSettings?.display_font ?? 'Bitter');
  const [headingFont, setHeadingFont] = useState(initialSettings?.heading_font ?? 'Carlito');
  const [textFont, setTextFont] = useState(initialSettings?.text_font ?? 'Carlito');
  const [selectedFontPack, setSelectedFontPack] = useState<string | null>('bitter-carlito');

  const prevValuesRef = useRef({
    fontSize: initialSettings?.font_size ?? 10,
    lineHeight: initialSettings?.line_height ?? '1.15 Condensed',
    displayFont: initialSettings?.display_font ?? 'Bitter',
    headingFont: initialSettings?.heading_font ?? 'Carlito',
    textFont: initialSettings?.text_font ?? 'Carlito',
  });

  useEffect(() => {
    if (initialSettings) {
      if (initialSettings.font_size !== undefined) {
        setFontSize(initialSettings.font_size);
        prevValuesRef.current.fontSize = initialSettings.font_size;
      }
      if (initialSettings.line_height !== undefined) {
        setLineHeight(initialSettings.line_height);
        prevValuesRef.current.lineHeight = initialSettings.line_height;
      }
      if (initialSettings.display_font !== undefined) {
        setDisplayFont(initialSettings.display_font);
        prevValuesRef.current.displayFont = initialSettings.display_font;
      }
      if (initialSettings.heading_font !== undefined) {
        setHeadingFont(initialSettings.heading_font);
        prevValuesRef.current.headingFont = initialSettings.heading_font;
      }
      if (initialSettings.text_font !== undefined) {
        setTextFont(initialSettings.text_font);
        prevValuesRef.current.textFont = initialSettings.text_font;
      }
    }
  }, [initialSettings]);

  const sendChangedSettings = (newValues: {
    font_size?: number;
    line_height?: string;
    display_font?: string;
    heading_font?: string;
    text_font?: string;
  }) => {
    const changed: any = {};
    const prev = prevValuesRef.current;

    if (newValues.font_size !== undefined && newValues.font_size !== prev.fontSize) {
      changed.font_size = newValues.font_size;
      prevValuesRef.current.fontSize = newValues.font_size;
    }
    if (newValues.line_height !== undefined && newValues.line_height !== prev.lineHeight) {
      changed.line_height = newValues.line_height;
      prevValuesRef.current.lineHeight = newValues.line_height;
    }
    if (newValues.display_font !== undefined && newValues.display_font !== prev.displayFont) {
      changed.display_font = newValues.display_font;
      prevValuesRef.current.displayFont = newValues.display_font;
    }
    if (newValues.heading_font !== undefined && newValues.heading_font !== prev.headingFont) {
      changed.heading_font = newValues.heading_font;
      prevValuesRef.current.headingFont = newValues.heading_font;
    }
    if (newValues.text_font !== undefined && newValues.text_font !== prev.textFont) {
      changed.text_font = newValues.text_font;
      prevValuesRef.current.textFont = newValues.text_font;
    }

    if (Object.keys(changed).length > 0) {
      onSettingsChange?.(changed);
    }
  };

  const [lineHeightOpen, setLineHeightOpen] = useState(false);
  const [displayFontOpen, setDisplayFontOpen] = useState(false);
  const [headingFontOpen, setHeadingFontOpen] = useState(false);
  const [textFontOpen, setTextFontOpen] = useState(false);

  const lineHeightRef = useRef<HTMLDivElement>(null);
  const displayFontRef = useRef<HTMLDivElement>(null);
  const headingFontRef = useRef<HTMLDivElement>(null);
  const textFontRef = useRef<HTMLDivElement>(null);

  const lineHeightOptions = ['1 Tight', '1.15 Condensed', '1.3 Standard', '1.5 Relaxed', '2 Spacious'];
  const fontOptions = ['Arial', 'Bitter', 'Noto Serif', 'Carlito', 'BreeSerif', 'EB Garamond', 'Lora', 'Inter', 'Source Sans', 'Neuton', 'Roboto'];

  const singleFontPacks: FontPack[] = [
    { id: 'noto-serif', name: 'Noto Serif', fonts: ['Noto Serif'] },
    { id: 'bitter', name: 'Bitter', fonts: ['Bitter'] },
  ];

  const mixedFontPacks: FontPack[] = [
    { id: 'bitter-carlito', name: 'Bitter', fonts: ['Carlito'], isMixed: true },
    { id: 'breeserif-inter', name: 'BreeSerif', fonts: ['Inter'], isMixed: true },
    { id: 'eb-garamond-lora', name: 'EB Garamond', fonts: ['Lora'], isMixed: true },
    { id: 'breeserif-bitter', name: 'BreeSerif', fonts: ['Bitter'], isMixed: true },
    { id: 'lora-source-sans', name: 'Lora', fonts: ['Source Sans'], isMixed: true },
    { id: 'neuton-roboto', name: 'Neuton', fonts: ['Roboto'], isMixed: true },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (lineHeightRef.current && !lineHeightRef.current.contains(event.target as Node)) {
        setLineHeightOpen(false);
      }
      if (displayFontRef.current && !displayFontRef.current.contains(event.target as Node)) {
        setDisplayFontOpen(false);
      }
      if (headingFontRef.current && !headingFontRef.current.contains(event.target as Node)) {
        setHeadingFontOpen(false);
      }
      if (textFontRef.current && !textFontRef.current.contains(event.target as Node)) {
        setTextFontOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="resume-editor__section-expanded-body">
      <div className="text-font__section">
        <div className="text-font__field">
          <label className="text-font__label">
            Font Size
            {fontSize !== 10 && (
              <button
                type="button"
                className="text-font__reset-btn"
                onClick={() => setFontSize(10)}
              >
                Reset
              </button>
            )}
          </label>
          <div className="text-font__number-input">
            <button
              className="text-font__number-btn"
              onClick={() => {
                const newSize = Math.max(7, fontSize - 0.5);
                setFontSize(newSize);
                sendChangedSettings({ font_size: newSize });
              }}
            >
              −
            </button>
            <input
              type="number"
              className="text-font__number-input-field"
              value={fontSize}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (!isNaN(value)) {
                  const rounded = Math.round(value * 2) / 2;
                  const clamped = Math.max(7, Math.min(13, rounded));
                  setFontSize(clamped);
                }
              }}
              onBlur={(e) => {
                const value = parseFloat(e.target.value);
                if (isNaN(value) || value < 7 || value > 13) {
                  setFontSize(10);
                  sendChangedSettings({ font_size: 10 });
                } else {
                  const rounded = Math.round(value * 2) / 2;
                  setFontSize(rounded);
                  sendChangedSettings({ font_size: rounded });
                }
              }}
              min={7}
              max={13}
              step={0.5}
            />
            <button
              className="text-font__number-btn"
              onClick={() => {
                const newSize = Math.min(13, fontSize + 0.5);
                setFontSize(newSize);
                sendChangedSettings({ font_size: newSize });
              }}
            >
              +
            </button>
          </div>
        </div>

        <div className="text-font__field">
          <label className="text-font__label">Line Height</label>
          <div className="text-font__dropdown" ref={lineHeightRef}>
            <button
              type="button"
              className="text-font__dropdown-trigger"
              onClick={() => setLineHeightOpen(!lineHeightOpen)}
            >
              <span className="text-font__dropdown-value">{lineHeight}</span>
              <span className="text-font__dropdown-arrow">▾</span>
            </button>
            {lineHeightOpen && (
              <div className="text-font__dropdown-menu">
                {lineHeightOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`text-font__dropdown-item ${lineHeight === option ? 'text-font__dropdown-item--selected' : ''}`}
                    onClick={() => {
                      setLineHeight(option);
                      setLineHeightOpen(false);
                      sendChangedSettings({ line_height: option });
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="text-font__section">
        <div className="text-font__field">
          <label className="text-font__label">Display</label>
          <div className="text-font__dropdown" ref={displayFontRef}>
            <button
              type="button"
              className="text-font__dropdown-trigger"
              onClick={() => setDisplayFontOpen(!displayFontOpen)}
            >
              <span className="text-font__dropdown-value">{displayFont}</span>
              <span className="text-font__dropdown-arrow">▾</span>
            </button>
            {displayFontOpen && (
              <div className="text-font__dropdown-menu">
                {fontOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`text-font__dropdown-item ${displayFont === option ? 'text-font__dropdown-item--selected' : ''}`}
                    onClick={() => {
                      setDisplayFont(option);
                      setDisplayFontOpen(false);
                      sendChangedSettings({ display_font: option });
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="text-font__field">
          <label className="text-font__label">Heading</label>
          <div className="text-font__dropdown" ref={headingFontRef}>
            <button
              type="button"
              className="text-font__dropdown-trigger"
              onClick={() => setHeadingFontOpen(!headingFontOpen)}
            >
              <span className="text-font__dropdown-value">{headingFont}</span>
              <span className="text-font__dropdown-arrow">▾</span>
            </button>
            {headingFontOpen && (
              <div className="text-font__dropdown-menu">
                {fontOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`text-font__dropdown-item ${headingFont === option ? 'text-font__dropdown-item--selected' : ''}`}
                    onClick={() => {
                      setHeadingFont(option);
                      setHeadingFontOpen(false);
                      sendChangedSettings({ heading_font: option });
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="text-font__field">
          <label className="text-font__label">Text</label>
          <div className="text-font__dropdown" ref={textFontRef}>
            <button
              type="button"
              className="text-font__dropdown-trigger"
              onClick={() => setTextFontOpen(!textFontOpen)}
            >
              <span className="text-font__dropdown-value">{textFont}</span>
              <span className="text-font__dropdown-arrow">▾</span>
            </button>
            {textFontOpen && (
              <div className="text-font__dropdown-menu">
                {fontOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`text-font__dropdown-item ${textFont === option ? 'text-font__dropdown-item--selected' : ''}`}
                    onClick={() => {
                      setTextFont(option);
                      setTextFontOpen(false);
                      sendChangedSettings({ text_font: option });
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="text-font__section">
        <h3 className="text-font__section-title">Single Font Packs</h3>
        <div className="text-font__packs-grid">
          {singleFontPacks.map((pack) => (
            <div
              key={pack.id}
              className={`text-font__pack-card ${selectedFontPack === pack.id ? 'text-font__pack-card--selected' : ''}`}
              onClick={() => {
                setSelectedFontPack(pack.id);
                if (pack.fonts.length === 1) {
                  const font = pack.fonts[0];
                  setDisplayFont(font);
                  setHeadingFont(font);
                  setTextFont(font);
                  sendChangedSettings({ display_font: font, heading_font: font, text_font: font });
                }
              }}
            >
              <div className="text-font__pack-name">{pack.name}</div>
              <div className="text-font__pack-preview">
                {pack.fonts.map((font, idx) => (
                  <div key={idx} className="text-font__pack-preview-text">{font}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-font__section">
        <h3 className="text-font__section-title">Mixed Fonts Packs (2 Fonts)</h3>
        <div className="text-font__packs-grid text-font__packs-grid--mixed">
          {mixedFontPacks.map((pack) => (
            <div
              key={pack.id}
              className={`text-font__pack-card ${selectedFontPack === pack.id ? 'text-font__pack-card--selected' : ''}`}
              onClick={() => {
                setSelectedFontPack(pack.id);
                const displayHeadingFont = pack.name;
                const textFont = pack.fonts[0];
                setDisplayFont(displayHeadingFont);
                setHeadingFont(displayHeadingFont);
                setTextFont(textFont);
                sendChangedSettings({ display_font: displayHeadingFont, heading_font: displayHeadingFont, text_font: textFont });
              }}
            >
              <div className="text-font__pack-name">{pack.name}</div>
              <div className="text-font__pack-preview">
                {pack.fonts.map((font, idx) => (
                  <div key={idx} className="text-font__pack-preview-text">{font}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

